import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNow } from '@/components/allocation/use-now';
import {
  AllocationWorkspace,
  type AllocationPlanController,
} from '@/components/allocation/allocation-workspace';
import { useHotelBoard } from '@/lib/disruptions/use-hotel-board';
import { useTransferBoard } from '@/lib/disruptions/use-disruptions';
import { RoomingListButton } from '@/components/documents/rooming-list-sheet';
import type { DisruptionDetail } from '@/lib/disruptions/types';
import type { IHotelBoard } from '@/components/hotels/types';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import { Link } from 'react-router';
import type { TransferLens } from './tower/tower-model';
import { stripsOf } from './tower/tower-transfer-board';
import { TransferStrip } from './tower/tower-transfer-panel';
import { EventAllocationActionBar } from './event-allocation-action-bar';
import { GoalBar } from './hotel-desk/goal-bar';
import { WorkQueue } from './hotel-desk/work-queue';
import { useHotelDesk } from './hotel-desk/use-hotel-desk';
import {
  PLAN_STALE_MS,
  useBookAllDisruption,
  useBuildDisruptionPlan,
  useDisruptionAllocationPlan,
} from './use-event-allocation';

// Disruption "Hotels" tab — remainder-first HOTEL DESK. The goal bar + the work
// queue (every PNR still owed an outcome, grouped by why, with Find hotel /
// Voucher / No offer per row) lead the view; the occupancy board, bulk tools and
// the shared allocation plan live one toggle away under "Hotels & tools". The
// desk empties the queue; the tools stay a lever, not the landing page. Deep
// links (`#hotels?hotel=…&transfer=…`) still open the tools. The SHUTTLE BOARD
// moved to the Transport tab (transport-desk T3) — each hotel keeps its strip
// plus an "Open Transport →" deep link (`#transport?hotel=…`).

type DeskMode = 'queue' | 'tools';

export function EventAllocationTab({
  disruptionUrn,
  focusHotel,
  transferLens,
}: {
  disruptionUrn: string;
  detail: DisruptionDetail;
  /** Deep link (`#hotels?hotel=…&transfer=…`): focus one hotel / a shuttle lens. */
  focusHotel?: string | undefined;
  transferLens?: TransferLens | undefined;
}) {
  const { t } = useTranslation('disruptions');
  const deskQuery = useHotelDesk(disruptionUrn);
  const desk = deskQuery.desk;

  // A deep link into a hotel / the shuttle plan opens the tools directly; an
  // all-settled desk also lands there (the queue would be an empty room).
  const deepLinked = !!focusHotel || !!transferLens;
  const [mode, setMode] = useState<DeskMode>(deepLinked ? 'tools' : 'queue');
  useEffect(() => {
    if (deepLinked) setMode('tools');
  }, [deepLinked]);
  const effectiveMode: DeskMode = mode === 'queue' && desk?.allSettled ? 'tools' : mode;

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      {deskQuery.isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : deskQuery.isError ? (
        <EmptyState title={t('hotels.error.title')} description={t('hotels.error.description')} />
      ) : desk ? (
        <GoalBar desk={desk} truncated={deskQuery.truncated} />
      ) : null}

      {desk ? (
        <div className="flex items-center gap-1" role="tablist" aria-label={t('hotels.mode.label')}>
          <ModeTab
            active={effectiveMode === 'queue'}
            onClick={() => setMode('queue')}
            testId="desk-mode-queue"
          >
            {t('hotels.mode.queue')}{' '}
            {desk.counts.NEEDS_HOTEL.pnrs + desk.counts.DECLINED.pnrs > 0 ? (
              <span className="ml-1 rounded-full bg-amber-100 px-1.5 text-[11px] font-semibold tabular-nums text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                {desk.counts.NEEDS_HOTEL.pnrs + desk.counts.DECLINED.pnrs}
              </span>
            ) : null}
          </ModeTab>
          <ModeTab
            active={effectiveMode === 'tools'}
            onClick={() => setMode('tools')}
            testId="desk-mode-tools"
          >
            {t('hotels.mode.tools')}
          </ModeTab>
        </div>
      ) : null}

      {effectiveMode === 'queue' && desk ? (
        desk.queue.length === 0 ? (
          <EmptyState
            title={t('hotels.empty.title')}
            description={t('hotels.empty.description')}
          />
        ) : (
          <WorkQueue queue={desk.queue} disruptionUrn={disruptionUrn} />
        )
      ) : null}

      {effectiveMode === 'tools' ? (
        <div className="min-h-0 flex-1">
          <ToolsSurface
            disruptionUrn={disruptionUrn}
            focusHotel={focusHotel}
            transferLens={transferLens}
          />
        </div>
      ) : null}
    </div>
  );
}

function ModeTab({
  active,
  onClick,
  testId,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'rounded-md px-3 py-1 text-sm transition-colors',
        active
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

/** The previous Hotels surface, intact: occupancy board + bulk tools + the shared
 *  plan + the shuttle panel — now one toggle away instead of the landing page. */
function ToolsSurface({
  disruptionUrn,
  focusHotel,
  transferLens,
}: {
  disruptionUrn: string;
  focusHotel?: string | undefined;
  transferLens?: TransferLens | undefined;
}) {
  const { t } = useTranslation('disruptions');
  const hotelBoard = useHotelBoard(disruptionUrn);
  // MASS ground transport: the shuttle board (plan + runs), WS-merged; folded per
  // hotel into an additive `transfer` strip on each occupancy card + the plan editor.
  const transfers = useTransferBoard(disruptionUrn);
  const strips = useMemo(() => stripsOf(transfers.data), [transfers.data]);
  const board = useMemo(
    () => ({
      ...hotelBoard,
      data: withTransfer(hotelBoard.data, strips),
    }),
    [hotelBoard, strips],
  );

  // The plan is SHARED across operators: stored server-side, loaded here (GET on
  // mount), live-updated over the disruption WS room into this same cache key.
  const planQuery = useDisruptionAllocationPlan(disruptionUrn);
  const buildMut = useBuildDisruptionPlan(disruptionUrn);
  const bookMut = useBookAllDisruption(disruptionUrn);
  const env = planQuery.data ?? null;
  const plan = env?.plan ?? null;
  const results = env?.results ?? null;
  const planning = buildMut.isPending || planQuery.isFetching;
  const booking = bookMut.isPending;

  // Freshness + attribution cue — who built/booked it, and how long ago.
  const now = useNow(!!env);
  const builtAtMs = env ? Date.parse(env.builtAt) : 0;
  const ageMs = env ? now - builtAtMs : 0;
  const stale = !!env && !results && ageMs >= PLAN_STALE_MS;
  const attribution = env
    ? results
      ? t('hotels.plan.bookedBy', { name: env.bookedByName ?? env.builtByName })
      : t('hotels.plan.builtBy', { name: env.builtByName, age: formatAge(ageMs, t) })
    : undefined;

  const controller: AllocationPlanController = {
    plan,
    results,
    planning,
    booking,
    buildPlan: () => buildMut.mutate(),
    bookAll: () => {
      if (plan) bookMut.mutate(plan);
    },
    buildError: buildMut.isError,
    bookError: bookMut.isError,
    attribution,
    stale,
    buildLabel: t('hotels.plan.buildLabel'),
    rebuildLabel: t('hotels.plan.rebuildLabel'),
    emptyHint: (
      <div className="space-y-2 text-muted-foreground">
        <p>
          <span className="font-medium text-foreground">{t('hotels.plan.buildLabel')}</span>{' '}
          {t('hotels.plan.hint.p1a')}{' '}
          <span className="font-medium text-foreground">{t('hotels.plan.hint.bookAll')}</span>{' '}
          {t('hotels.plan.hint.p1b')}
        </p>
        <p>
          {t('hotels.plan.hint.p2a')}{' '}
          <span className="font-medium text-foreground">{t('allocationBar.action')}</span>{' '}
          {t('hotels.plan.hint.p2b')}{' '}
          <span className="font-medium text-foreground">{t('hotels.mode.queue')}</span>{' '}
          {t('hotels.plan.hint.p2c')}
        </p>
      </div>
    ),
  };

  // The meal-voucher mass bar (EventVoucherActionBar) moved HOME to the Care tab
  // (feat/care-desk K5) — the Hotels tab carries nothing care-related anymore.
  return (
    <AllocationWorkspace
      board={board}
      controller={controller}
      actionBar={<EventAllocationActionBar disruptionUrn={disruptionUrn} />}
      /* transport-desk (T3): the shuttle board (TransferPanel) moved to the
         Transport tab — the single source, re-homed (not copied). The per-hotel
         strips stay here as context; a shuttle deep-link gets a pointer. */
      transferPanel={
        transferLens ? (
          <p className="py-2 text-xs text-muted-foreground" data-testid="transfer-moved-note">
            {t('hotels.transfer.movedPre')}{' '}
            <Link
              to={{ hash: transportHash(focusHotel) }}
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              {t('hotels.transfer.movedLink')}
            </Link>{' '}
            {t('hotels.transfer.movedPost')}
          </p>
        ) : null
      }
      toolsOpenHint={!!transferLens || (transfers.data?.plan != null && strips.size > 0)}
      initialHotel={focusHotel}
      renderHotelExtra={(h) => (
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          {h.transfer ? (
            <>
              <TransferStrip strip={h.transfer} />
              <Link
                to={{ hash: transportHash(h.transfer.hotelUrn) }}
                className="whitespace-nowrap text-xs font-medium text-primary underline-offset-2 hover:underline"
                data-testid="open-transport-link"
              >
                {t('hotels.transfer.openTransport')}
              </Link>
            </>
          ) : null}
          {/* #809: the hotel's rooming list (printable sheet + CSV). */}
          <span className="ml-auto">
            <RoomingListButton disruptionUrn={disruptionUrn} hotelUrn={h.hotelUrn} hotelName={h.name} />
          </span>
        </div>
      )}
    />
  );
}

/** Deep link into the Transport tab (`#transport?hotel=<urn>` — the hash-param grammar). */
function transportHash(hotelUrn?: string | undefined): string {
  return `#transport${hotelUrn ? `?hotel=${encodeURIComponent(hotelUrn)}` : ''}`;
}

/** Attach the per-hotel shuttle strip to the occupancy board rows (additive). */
function withTransfer(
  data: IHotelBoard | undefined,
  strips: ReturnType<typeof stripsOf>,
): IHotelBoard | undefined {
  if (!data || strips.size === 0) return data;
  return {
    ...data,
    hotels: data.hotels.map((h) => {
      const t = strips.get(h.hotelUrn);
      return t ? { ...h, transfer: t } : h;
    }),
  };
}

/** "just now" / "3 min ago" / "1 h 5 min ago" — the plan's age. Holds keys, never
 *  language: the caller passes the live `t`. */
function formatAge(ms: number, t: TFunction<'disruptions'>): string {
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return t('hotels.age.justNow');
  if (mins < 60) return t('hotels.age.minutes', { n: mins });
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? t('hotels.age.hoursMinutes', { h, m }) : t('hotels.age.hours', { h });
}
